export interface ChangedKeyInfo {
  key: string;
  age: number;
}

export interface SettingCache {
  version: number;
  changedKeys: ChangedKeyInfo[];
}

/**
 * Decodes a Base64-encoded string representing the setting cache metadata into a structured object.
 * @param base64 - The Base64-encoded string containing the setting cache metadata.
 * @returns The decoded setting cache object.
 */
export function decodeSettingCacheMetadata(base64: string): SettingCache {
  // Prevent decoding if input is empty or null
  if (!base64) {
    return { version: 0, changedKeys: [] };
  }

  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const view = new DataView(bytes.buffer);
  let offset = 0;

  // Need at least 2 bytes for version
  if (len < 2) {
    return { version: 0, changedKeys: [] };
  }

  // 1. Read Version (2 bytes, Little Endian)
  const version = view.getUint16(offset, true);
  offset += 2;

  const changedKeys: ChangedKeyInfo[] = [];
  const textDecoder = new TextDecoder('utf-8');

  // 2. Read Changed Keys until we reach the end of the byte array
  while (offset < len) {
    // Safety check: Ensure we have at least 2 bytes for the string length
    if (offset + 2 > len) break;

    // Read the length of the key string (2 bytes, Little Endian)
    const strLen = view.getUint16(offset, true);
    offset += 2;

    // Safety check: Ensure we have enough bytes for the string content
    if (offset + strLen > len) break;

    // Read the key string
    const strBytes = new Uint8Array(bytes.buffer, offset, strLen);
    const keyString = textDecoder.decode(strBytes);
    offset += strLen;

    // Safety check: Ensure we have at least 1 byte for the age
    if (offset + 1 > len) break;

    // Read the age (1 byte)
    const age = view.getUint8(offset);
    offset += 1;

    changedKeys.push({ key: keyString, age: age });
  }

  return { version, changedKeys };
}
